import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { allArticles, type ArticleContent } from '@/pages/articles/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleTemplateProps {
  article: ArticleContent;
}

function generateArticleSchema(article: ArticleContent, siteUrl: string): Record<string, unknown> {
  const articlePath = article.slug === 'pandabuy-spreadsheet-guide'
    ? `/${article.slug}`
    : `/articles/${article.slug}`;
  const articleUrl = `${siteUrl}${articlePath}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    url: articleUrl,
    datePublished: '2026-06-20',
    dateModified: '2026-07-01',
    author: {
      '@type': 'Organization',
      name: 'Pandabuy Spreadsheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pandabuy Spreadsheet',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    about: {
      '@type': 'Thing',
      name: article.category,
    },
  };
}

export default function ArticleTemplate({ article }: ArticleTemplateProps) {
  const siteUrl = (import.meta.env as Record<string, string>).VITE_SITE_URL || 'https://pandabuy.team';
  const schema = generateArticleSchema(article, siteUrl);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [article.slug]);

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
        <section className="relative pt-24 pb-10 md:pt-32 md:pb-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-400">{article.category}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-3">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.description}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> Updated June 30, 2026</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
              <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium whitespace-nowrap">{article.category}</span>
            </div>
          </div>
        </section>

        <section className="bg-background-100 pb-6">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden">
              <img src={article.heroImage} alt={article.title} className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
          </div>
        </section>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="space-y-8">
              {article.sections.map((section) => (
                <section key={section.id}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3" id={section.id}>
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">{p}</p>
                  ))}
                </section>
              ))}

              {article.tips.length > 0 && (
                <div className="bg-background-100 border border-background-200/60 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">Quick Tips</h3>
                  <ul className="space-y-3">
                    {article.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0 mt-0.5">
                          <i className={`${tip.icon} text-accent-600 text-sm`}></i>
                        </div>
                        <span className="text-sm text-foreground-700 leading-relaxed">{tip.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {article.faqs.length > 0 && (
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">Frequently Asked Questions</h2>
                  <div className="space-y-4 mt-4">
                    {article.faqs.map((faq, i) => (
                      <div key={i} className="bg-background-100 border border-background-200/60 rounded-lg p-4 md:p-5">
                        <h4 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div className="bg-accent-50 border border-accent-200/60 rounded-lg p-5 md:p-6 text-center">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">Ready to Start Exploring?</h3>
                <p className="text-sm text-foreground-600 mb-4">Return to our homepage to browse all pandabuyspreadsheet categories and discover more curated guides.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-home-line"></i>Back to Homepage
                  </Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-foreground-950 bg-accent-400 hover:bg-accent-500 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                    pandabuy sheet<i className="ri-arrow-right-up-line ml-1.5"></i>
                  </a>
                </div>
              </div>

              <div className="border-t border-background-200/60 pt-8 mt-4">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">More Shopping Guides</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {allArticles.filter(a => a.slug !== article.slug).slice(0, 6).map((a) => (
                    <Link key={a.slug} to={`/articles/${a.slug}`} className="flex items-center gap-3 p-3 bg-background-100 border border-background-200/60 rounded-lg hover:border-primary-300/60 transition-colors cursor-pointer">
                      <span className="text-xs text-primary-600 font-medium whitespace-nowrap">{a.category}</span>
                      <span className="text-sm text-foreground-700 truncate">{a.title.split(':')[0]}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}